import React, { useState, useEffect, useRef } from 'react';
import { useFocusTrap } from '../../hooks/useFocusTrap';

/**
 * Props for the CameraCaptureModal component.
 */
interface CameraCaptureModalProps {
  onCapture: (blob: Blob) => void;
  onClose: () => void;
}

/**
 * A modal component for capturing images using the device's camera.
 * @param {CameraCaptureModalProps} props - The props for the component.
 * @returns {React.ReactElement} - The rendered camera capture modal.
 */
const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({ onCapture, onClose }) => {
  const [isShowing, setIsShowing] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useFocusTrap(modalRef, isShowing);

  useEffect(() => {
    setIsShowing(true);

    const startCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }, // Default to back camera
        });
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (err) {
        console.error("Error accessing camera:", err);
        setError("Could not access the camera. Please check permissions and try again.");
      }
    };

    startCamera();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      // Cleanup: stop all video tracks to turn off the camera light.
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []); // Run only on mount and unmount

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Set canvas dimensions to match the video stream.
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (context) {
      // Draw the current video frame onto the canvas.
      context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
      // Convert canvas to a Blob and pass it to the parent.
      canvas.toBlob((blob) => {
        if (blob) {
          onCapture(blob);
        }
        onClose();
      }, 'image/jpeg', 0.95);
    }
  };

  const handleClose = () => {
    setIsShowing(false);
    setTimeout(onClose, 300);
  }

  return (
    <div
      className={`fixed inset-0 bg-black z-50 flex justify-center items-center transition-opacity duration-300 ${isShowing ? 'bg-opacity-80' : 'bg-opacity-0'}`}
      aria-labelledby="camera-modal-title"
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        className={`bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl m-4 transform transition-all duration-300 ease-out flex flex-col ${isShowing ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="p-4 border-b border-gray-700 flex-shrink-0">
          <div className="flex justify-between items-center">
            <h2 id="camera-modal-title" className="text-lg font-bold text-white">Capture Image</h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-white rounded-full p-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-indigo-500"
              aria-label="Close camera"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </header>

        <main className="p-4 flex-grow relative">
          {error ? (
            <div className="flex items-center justify-center h-full text-red-400 bg-red-900/50 rounded-lg p-4">{error}</div>
          ) : (
            <video ref={videoRef} autoPlay playsInline className="w-full h-full rounded-md object-cover"></video>
          )}
          <canvas ref={canvasRef} className="hidden"></canvas>
        </main>

        <footer className="p-4 flex-shrink-0 flex justify-center">
          <button
            onClick={handleCapture}
            disabled={!!error || !stream}
            className="w-20 h-20 bg-white rounded-full border-4 border-gray-400 focus:outline-none focus:ring-4 focus:ring-indigo-500/50 disabled:bg-gray-500 disabled:cursor-not-allowed"
            aria-label="Take picture"
          ></button>
        </footer>
      </div>
    </div>
  );
};

export default CameraCaptureModal;
